import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { IoIosClose } from "react-icons/io";
import { BsCheck2, BsThreeDotsVertical } from "react-icons/bs";
import { RiExpandLeftFill, RiExpandRightFill } from "react-icons/ri";
import { RxReset } from "react-icons/rx";

const getSpan = (cell) => ({
    rowspan: cell?.rowspan === undefined ? 1 : cell.rowspan,
    colspan: cell?.colspan === undefined ? 1 : cell.colspan,
})

const isOrigin = (cell) => {
    if (!cell) return false
    const { rowspan, colspan } = getSpan(cell)
    return rowspan > 0 && colspan > 0
}

const findOriginOf = (data, r, c) => {
    if (isOrigin(data[r]?.[c])) return { r, c }
    for (let rr = r; rr >= 0; rr--) {
        for (let cc = c; cc >= 0; cc--) {
            if (rr === r && cc === c) continue
            const cand = data[rr]?.[cc]
            if (!isOrigin(cand)) continue
            const { rowspan, colspan } = getSpan(cand)
            if (rr + rowspan > r && cc + colspan > c) return { r: rr, c: cc }
        }
    }
    return null
}

const explodeMerge = (data, r, c) => {
    const cell = data[r]?.[c]
    if (!isOrigin(cell)) return
    const { rowspan, colspan } = getSpan(cell)
    for (let rr = r; rr < r + rowspan; rr++) {
        for (let cc = c; cc < c + colspan; cc++) {
            if (data[rr] && data[rr][cc]) {
                data[rr][cc] = { ...data[rr][cc], rowspan: 1, colspan: 1 }
            }
        }
    }
}

const applyMerge = (data, row, col, newR, newC) => {
    if (newR < 1 || newC < 1) return null
    if (row + newR > data.length) return null
    if (col + newC > (data[0]?.length ?? 0)) return null

    const newData = data.map(rowData => rowData.map(cell => ({ ...cell })))

    const exploded = new Set()
    for (let r = row; r < row + newR; r++) {
        for (let c = col; c < col + newC; c++) {
            const owner = findOriginOf(newData, r, c)
            if (!owner) continue
            const key = `${owner.r},${owner.c}`
            if (exploded.has(key)) continue
            exploded.add(key)
            explodeMerge(newData, owner.r, owner.c)
        }
    }

    for (let r = row; r < row + newR; r++) {
        for (let c = col; c < col + newC; c++) {
            if (r === row && c === col) continue
            if (r === row) {
                newData[r][c] = { ...newData[r][c], rowspan: 1, colspan: 0 }
            } else {
                newData[r][c] = { ...newData[r][c], rowspan: 0, colspan: 1 }
            }
        }
    }

    newData[row][col] = { ...newData[row][col], rowspan: newR, colspan: newC }
    return newData
}

export default function Table({ object, handleTabelData, handleFormData, preview, tableData, data, formUse, viewOnly, rowIndex, colIndex, handleChangeCellType, handleChangeCellOptions, handleChangeColumnName, handleChangeColumnType, handleChangeColumnOptions, handleChangeColumnAlign, handleChangeColumnHeaderAlign, onUndoTable, onResetMerges, canUndoTable }) {


    const [tableobject, setTableObject] = useState({})
    const [selectedRow, setSelectedRow] = useState(null)
    const [selectedColumn, setSelectedColumn] = useState(null)
    const [image, setImage] = useState(null)
    const [cellTypePickerOpen, setCellTypePickerOpen] = useState(null)
    const [columnPickerOpen, setColumnPickerOpen] = useState(null)
    const editorMode = !preview && !viewOnly && !!handleChangeCellType

    useEffect(() => {
        preview && preview ? setTableObject(object) :
            setTableObject({
                ...object, tableData: object?.tableData?.map((data) => {
                    return data?.map((each) => {
                        return { ...each, edit: false}
                    })
                })
            })
    }, [object, object.columnNames, object.columns, object.tableData, object.columnNames])

    useEffect(() => {
        if (!cellTypePickerOpen) return
        const close = () => setCellTypePickerOpen(null)
        window.addEventListener('scroll', close, true)
        window.addEventListener('resize', close)
        return () => {
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('resize', close)
        }
    }, [cellTypePickerOpen])

    useEffect(() => {
        if (!columnPickerOpen) return
        const close = () => setColumnPickerOpen(null)
        window.addEventListener('scroll', close, true)
        window.addEventListener('resize', close)
        return () => {
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('resize', close)
        }
    }, [columnPickerOpen])

    const openCellPickerForElement = (el, row, col) => {
        const rect = el ? el.getBoundingClientRect() : null
        setSelectedRow(row)
        setSelectedColumn(col)
        setCellTypePickerOpen({ row, col, rect: rect ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right } : null })
    }

    const openColumnPickerForElement = (el, col) => {
        const rect = el ? el.getBoundingClientRect() : null
        setColumnPickerOpen({ col, rect: rect ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right } : null })
    }

    useEffect(() => {
        formUse && handleFormData && handleFormData(object.content+"-"+rowIndex+colIndex, object.tableData.map((row, i) => {
            let obj = {}
            obj["row"] = i + 1
            row.forEach((cell, index) => {
                const type = cell.type || object.columnsType[index]
                if (type === "toggle") {
                    obj[object.columnNames[index]] = false
                } else if (type === "input" || type === "dropdown" || type === "date") {
                    obj[object.columnNames[index]] = ""
                } else {
                    obj[object.columnNames[index]] = cell["content"]
                }
            })
            return obj
        }))
    }, [])

    const handleEdit = (rowIndex, columnIndex) => {
        const tableData = { ...tableobject }
        setTableObject({
            ...tableobject, tableData: tableData.tableData.map((data, index) => {
                return index === rowIndex ? data.map((each, i) => {
                    return i === columnIndex ? { ...each, edit: true, originalContent: each.content } : each
                }) : data
            })
        })
    }

    const handleChangeData = (e, rowIndex, columnIndex) => {
        const tableData = { ...tableobject }
        setTableObject({
            ...tableobject, tableData: tableData.tableData.map((data, index) => {
                return index === rowIndex ? data.map((each, i) => {
                    return i === columnIndex ? { ...each, content: e.target.value } : each
                }) : data
            })
        })
    }

    const handleCancel = (rowIndex, columnIndex) => {
        setTableObject({
            ...tableobject, tableData: tableobject.tableData.map((data, index) => {
                return index === rowIndex ? data.map((each, i) => {
                    if (i !== columnIndex) return each
                    const restored = each.originalContent !== undefined ? each.originalContent : each.content
                    const { originalContent, ...rest } = each
                    return { ...rest, edit: false, content: restored }
                }) : data
            })
        })
    }
    const handleSave = (rowindex, colindex) => {
        const newData = tableobject.tableData.map((data, index) => {
            return index === rowindex ? data.map((each, i) => {
                if (i !== colindex) return each
                const { originalContent, ...rest } = each
                return { ...rest, edit: false }
            }) : data
        })
        handleTabelData(tableobject.id, newData)
        setTableObject({ ...tableobject, tableData: newData })
    }

    const increaseColSpan = () => {
        if (selectedRow === null || selectedColumn === null) return
        const cell = tableobject.tableData[selectedRow]?.[selectedColumn]
        if (!isOrigin(cell)) return
        const { rowspan, colspan } = getSpan(cell)
        const newData = applyMerge(tableobject.tableData, selectedRow, selectedColumn, rowspan, colspan + 1)
        if (!newData) return
        setTableObject({ ...tableobject, tableData: newData })
        handleTabelData(tableobject.id, newData)
    }

    const increaseRowSpan = () => {
        if (selectedRow === null || selectedColumn === null) return
        const cell = tableobject.tableData[selectedRow]?.[selectedColumn]
        if (!isOrigin(cell)) return
        const { rowspan, colspan } = getSpan(cell)
        const newData = applyMerge(tableobject.tableData, selectedRow, selectedColumn, rowspan + 1, colspan)
        if (!newData) return
        setTableObject({ ...tableobject, tableData: newData })
        handleTabelData(tableobject.id, newData)
    }

    const mergeLeft = () => {
        if (selectedRow === null || selectedColumn === null) return
        if (selectedColumn === 0) return
        const leftOwner = findOriginOf(tableobject.tableData, selectedRow, selectedColumn - 1)
        if (!leftOwner) return
        const { rowspan, colspan } = getSpan(tableobject.tableData[leftOwner.r][leftOwner.c])
        const newData = applyMerge(tableobject.tableData, leftOwner.r, leftOwner.c, rowspan, colspan + 1)
        if (!newData) return
        setTableObject({ ...tableobject, tableData: newData })
        handleTabelData(tableobject.id, newData)
        setSelectedRow(leftOwner.r)
        setSelectedColumn(leftOwner.c)
    }

    const mergeUp = () => {
        if (selectedRow === null || selectedColumn === null) return
        if (selectedRow === 0) return
        const upOwner = findOriginOf(tableobject.tableData, selectedRow - 1, selectedColumn)
        if (!upOwner) return
        const { rowspan, colspan } = getSpan(tableobject.tableData[upOwner.r][upOwner.c])
        const newData = applyMerge(tableobject.tableData, upOwner.r, upOwner.c, rowspan + 1, colspan)
        if (!newData) return
        setTableObject({ ...tableobject, tableData: newData })
        handleTabelData(tableobject.id, newData)
        setSelectedRow(upOwner.r)
        setSelectedColumn(upOwner.c)
    }

    const handleFileChange = (e, rowIndex, columnIndex) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                const tableData = { ...tableobject }
                let data = {
                  ...tableobject,
                  tableData: tableData.tableData.map((data, index) => {
                    return index === rowIndex
                      ? data.map((each, i) => {
                          return i === columnIndex
                            ? { ...each, src: reader.result }
                            : each;
                        })
                      : data;
                  }),
                };
                setTableObject(data)
                setImage(true)
                handleTabelData(tableobject.id, data.tableData)
            }
            reader.readAsDataURL(file);
        }
    }



    const columnCount = tableobject?.columnNames?.length || 1
    const equalColWidth = `${(100 / columnCount).toFixed(4)}%`
    return (
        <div className='form_table_container' style={{ overflow: 'hidden', width: '100%' }}>
            {editorMode && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '2px 6px 6px' }}>
                    <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                        Tip: right-click any cell or column header for more options
                    </span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {onUndoTable && (
                            <button
                                type='button'
                                title='Undo last table change'
                                className='btn btn-light btn-sm d-flex align-items-center justify-content-center gap-1'
                                style={{ fontSize: '11px', padding: '2px 8px' }}
                                disabled={!canUndoTable}
                                onClick={onUndoTable}
                            >
                                <RxReset style={{ height: '11px', width: '11px' }} />
                                Undo
                            </button>
                        )}
                        {onResetMerges && (
                            <button
                                type='button'
                                title='Reset all cell merges'
                                className='btn btn-light btn-sm'
                                style={{ fontSize: '11px', padding: '2px 8px' }}
                                onClick={onResetMerges}
                            >
                                Reset merges
                            </button>
                        )}
                    </div>
                </div>
            )}
            <table style={{ width: "100%", tableLayout: 'fixed', borderCollapse: 'collapse' }} onMouseLeave={() => { if (editorMode && !cellTypePickerOpen && !columnPickerOpen) { setSelectedRow(null); setSelectedColumn(null); } }}>
                <colgroup>
                    {tableobject?.columnNames?.map((_, index) => (
                        <col key={index} style={{ width: equalColWidth }} />
                    ))}
                </colgroup>
                {object.showHeader && <thead className='form_table_header'>
                    <tr>
                        {tableobject?.columnNames?.map((columnName, index) => {
                            const perColumnHeaderAlign = tableobject?.columnsHeaderAlign?.[index]
                            const headerAlign = perColumnHeaderAlign || tableobject?.headerAlign || "start"
                            const isHeaderHighlighted = !!(columnPickerOpen && columnPickerOpen.col === index)
                            return <td key={index} style={{
                                border: "0.4px solid #D3D3D3",
                                boxShadow: isHeaderHighlighted ? "inset 0 0 0 1.5px #525252" : undefined,
                                backgroundColor: isHeaderHighlighted ? "#e9ecef" : undefined,
                                position: 'relative',
                                fontSize: `14px`,
                                padding: '6px',
                                wordWrap: 'break-word',
                                overflowWrap: 'anywhere',
                                textAlign: headerAlign,
                                cursor: editorMode ? 'context-menu' : 'default'
                            }}
                                onContextMenu={(e) => {
                                    if (!editorMode || !handleChangeColumnName) return
                                    e.preventDefault()
                                    openColumnPickerForElement(e.currentTarget, index)
                                }}
                            >{columnName}</td>
                        })}
                    </tr>
                </thead>}
                <tbody>
                    {tableobject?.tableData?.map((row, index) => {
                        return <tr key={index}>
                            {row.map((column, i) => {
                                const isCellHighlighted = !!(cellTypePickerOpen && cellTypePickerOpen.row === index && cellTypePickerOpen.col === i)
                                return column.rowspan === 0 || column.colspan === 0 ? null : <td rowSpan={column?.rowspan} colSpan={column?.colspan} style={{
                                    border: `0.4px solid #D3D3D3`,
                                    boxShadow: isCellHighlighted ? "inset 0 0 0 1.5px #525252" : undefined,
                                    backgroundColor: isCellHighlighted ? "#e9ecef" : undefined,
                                    wordWrap: 'break-word',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal',
                                    fontSize: `14px`,
                                    padding: "8px",
                                    position: "relative",
                                    textAlign: column?.textAlign || "start",
                                    verticalAlign: 'top',
                                }} key={i}
                                onContextMenu={(e) => {
                                    if (!editorMode || !handleChangeCellType) return
                                    e.preventDefault()
                                    openCellPickerForElement(e.currentTarget, index, i)
                                }}
                                onMouseOver={(e) => {
                                        if (!preview && !viewOnly && !isCellHighlighted) {
                                            e.currentTarget.style.backgroundColor = "#f0f0f0";
                                            e.currentTarget.style.cursor = "pointer";
                                    }
                                    if (!cellTypePickerOpen && (selectedRow !== index || selectedColumn !== i)) {
                                        setSelectedRow(index);
                                        setSelectedColumn(i);
                                    }
                                }
                                }
                                onMouseOut={(e) => {
                                        if (!preview && !viewOnly && !isCellHighlighted) {
                                            e.currentTarget.style.backgroundColor = "white";
                                            e.currentTarget.style.cursor = "default";
                                    }
                                    }
                                }
                                >
                                    {selectedColumn === i && selectedRow === index && !preview && !viewOnly && <>
                                        {handleChangeCellType && <BsThreeDotsVertical title="Cell type" style={{ position: "absolute", left: "2px", top: "2px", height: "12px", width: "12px", cursor: "pointer" }} onClick={(e) => {
                                            e.stopPropagation()
                                            const tdEl = e.currentTarget.closest('td')
                                            const rect = tdEl ? tdEl.getBoundingClientRect() : null
                                            setCellTypePickerOpen({ row: index, col: i, rect: rect ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right } : null })
                                        }} />}
                                        {(i + (column.colspan || 1)) < row.length && <RiExpandRightFill title="Merge right" style={{ position: "absolute", right: "2px", top: "50%", transform: "translateY(-50%)", height: "12px", width: "12px", cursor: "pointer" }} onClick={increaseColSpan} />}
                                        {i > 0 && <RiExpandLeftFill title="Merge left" style={{ position: "absolute", left: "2px", top: "50%", transform: "translateY(-50%)", height: "12px", width: "12px", cursor: "pointer" }} onClick={mergeLeft} />}
                                        {index > 0 && <RiExpandLeftFill title="Merge up" style={{ position: "absolute", left: "50%", top: "2px", transform: "translateX(-50%) rotate(90deg)", height: "12px", width: "12px", cursor: "pointer" }} onClick={mergeUp} />}
                                        {(index + (column.rowspan || 1)) < tableobject.tableData.length && <RiExpandLeftFill title="Merge down" style={{ position: "absolute", left: "50%", bottom: "2px", transform: "translateX(-50%) rotate(-90deg)", height: "12px", width: "12px", cursor: "pointer" }} onClick={increaseRowSpan} />}
                                    </>
                                    }
                                    {(() => {
                                        const cellType = column.type || object.columnsType[i]
                                        const cellOptionsStr = column.options !== undefined ? column.options : (object.columnsOptions && object.columnsOptions[i])
                                        const cellOptions = cellOptionsStr ? cellOptionsStr.split(",").map(opt => opt.trim()).filter(opt => opt) : []
                                        if (cellType === "text") {
                                            return !preview && column.edit ?
                                                <div className='d-flex gap-1 align-items-center'>
                                                    <textarea type='text' value={column.content} onChange={(e) => handleChangeData(e, index, i)} className='form-control' />
                                                    <BsCheck2 color='green' style={{ height: "20px", width: "20px" }} onClick={() => handleSave(index, i)} />
                                                    <IoIosClose color='red' style={{ height: "30px", width: "30px" }} onClick={() => handleCancel(index, i)} />
                                                </div> :
                                                <FixedText index={index} i={i} preview={preview} value={column.content} handleEdit={handleEdit} />
                                        }
                                        if (cellType === "toggle") {
                                            return <Toggle tableData={tableData && tableData} keyName={tableobject.columnNames[i]}
                                                tableName={tableobject.content +"-"+ rowIndex + colIndex}
                                                row={index}
                                                data={data && data[object?.content + "-"+ rowIndex + colIndex] && data[object?.content + "-"+ rowIndex + colIndex][index][object.columnNames[i]]}
                                                viewOnly={viewOnly}
                                            />
                                        }
                                        if (cellType === "image") {
                                            return column?.src ?
                                                <img src={column.src} alt="table_image" style={{ maxWidth: "100%", width: "100%", height: "auto", objectFit: "contain", borderRadius: "8px" }} />
                                                :
                                                <input type="file" accept="image/*" style={{ maxWidth: "100%", width: "100%", height: "auto", borderRadius: "8px", cursor: "pointer" }} onChange={(e) => handleFileChange(e, index, i)} />
                                        }
                                        if (cellType === "dropdown") {
                                            return <DropdownCell tableData={tableData && tableData} keyName={tableobject.columnNames[i]}
                                                tableName={tableobject.content +"-"+ rowIndex + colIndex}
                                                row={index}
                                                data={data && data[object?.content + "-"+ rowIndex + colIndex] && data[object?.content + "-"+ rowIndex + colIndex][index][object.columnNames[i]]}
                                                viewOnly={viewOnly}
                                                options={cellOptions}
                                            />
                                        }
                                        if (cellType === "date") {
                                            return <DateCell tableData={tableData && tableData} keyName={tableobject.columnNames[i]}
                                                tableName={tableobject.content +"-"+ rowIndex + colIndex}
                                                row={index}
                                                data={data && data[object?.content + "-"+ rowIndex + colIndex] && data[object?.content + "-"+ rowIndex + colIndex][index][object.columnNames[i]]}
                                                viewOnly={viewOnly}
                                            />
                                        }
                                        return <TextInput tableData={tableData && tableData} keyName={tableobject.columnNames[i]}
                                            tableName={tableobject.content +"-"+ rowIndex + colIndex}
                                            row={index}
                                            data={data && data[object?.content + "-"+ rowIndex + colIndex] && data[object?.content + "-"+ rowIndex + colIndex][index][object.columnNames[i]]}
                                            viewOnly={viewOnly}
                                        />
                                    })()}
                                </td>
                            })}
                        </tr>
                    })}
                </tbody>
            </table>
            {cellTypePickerOpen && cellTypePickerOpen.rect && !preview && !viewOnly && handleChangeCellType && (() => {
                const popupWidth = 200
                const popupHeight = 130
                const margin = 8
                const r = cellTypePickerOpen.rect
                const flipDown = r.bottom + popupHeight + margin > window.innerHeight
                const top = flipDown ? Math.max(margin, r.top - popupHeight - 4) : r.bottom + 4
                const left = Math.min(Math.max(margin, r.left), window.innerWidth - popupWidth - margin)
                const cell = tableobject?.tableData?.[cellTypePickerOpen.row]?.[cellTypePickerOpen.col]
                if (!cell) return null
                const currentType = cell.type || object.columnsType[cellTypePickerOpen.col] || "text"
                const currentOptions = cell.options !== undefined ? cell.options : ""
                return createPortal(
                    <div onClick={(e) => e.stopPropagation()} style={{
                        position: "fixed",
                        top,
                        left,
                        width: popupWidth,
                        zIndex: 10000,
                        backgroundColor: "white",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "6px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                    }}>
                        <div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "4px" }}>Cell Type</div>
                        <select
                            className='form-select form-select-sm'
                            value={currentType}
                            onChange={(e) => {
                                handleChangeCellType(tableobject.id, cellTypePickerOpen.row, cellTypePickerOpen.col, e.target.value)
                                if (e.target.value !== "dropdown") {
                                    setCellTypePickerOpen(null)
                                }
                            }}
                            style={{ fontSize: "11px" }}
                        >
                            <option value="text">text</option>
                            <option value="toggle">toggle</option>
                            <option value="input">input</option>
                            <option value="image">image</option>
                            <option value="dropdown">dropdown</option>
                            <option value="date">date</option>
                        </select>
                        {currentType === "dropdown" && handleChangeCellOptions && (
                            <div style={{ marginTop: "6px" }}>
                                <div style={{ fontSize: "10px", color: "gray", marginBottom: "2px" }}>Options (comma separated)</div>
                                <input
                                    className='form-control form-control-sm'
                                    style={{ fontSize: "11px" }}
                                    placeholder='e.g. Yes, No, Maybe'
                                    value={currentOptions}
                                    onChange={(e) => handleChangeCellOptions(tableobject.id, cellTypePickerOpen.row, cellTypePickerOpen.col, e.target.value)}
                                />
                            </div>
                        )}
                        <button
                            className='btn btn-sm btn-light w-100'
                            style={{ fontSize: "10px", marginTop: "6px", padding: "2px 4px", border: "1px solid #ccc" }}
                            onClick={() => setCellTypePickerOpen(null)}
                        >Done</button>
                    </div>,
                    document.body
                )
            })()}
            {columnPickerOpen && columnPickerOpen.rect && editorMode && handleChangeColumnName && (() => {
                const popupWidth = 240
                const popupHeight = 280
                const margin = 8
                const r = columnPickerOpen.rect
                const flipDown = r.bottom + popupHeight + margin > window.innerHeight
                const top = flipDown ? Math.max(margin, r.top - popupHeight - 4) : r.bottom + 4
                const left = Math.min(Math.max(margin, r.left), window.innerWidth - popupWidth - margin)
                const ci = columnPickerOpen.col
                const currentName = tableobject?.columnNames?.[ci] ?? ""
                const currentType = tableobject?.columnsType?.[ci] || "text"
                const currentOptions = (tableobject?.columnsOptions && tableobject.columnsOptions[ci]) || ""
                const currentHeaderAlign = tableobject?.columnsHeaderAlign?.[ci] || tableobject?.headerAlign || "start"
                const currentCellAlign = tableobject?.tableData?.[0]?.[ci]?.textAlign || "start"
                return createPortal(
                    <div onClick={(e) => e.stopPropagation()} style={{
                        position: "fixed",
                        top,
                        left,
                        width: popupWidth,
                        zIndex: 10000,
                        backgroundColor: "white",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "8px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                    }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold", marginBottom: "6px" }}>Column {ci + 1}</div>
                        <div style={{ fontSize: "10px", color: "gray", marginBottom: "2px" }}>Name</div>
                        <input
                            className='form-control form-control-sm'
                            style={{ fontSize: "11px", marginBottom: "6px" }}
                            value={currentName}
                            onChange={(e) => handleChangeColumnName && handleChangeColumnName(e, ci, tableobject.id)}
                        />
                        <div style={{ fontSize: "10px", color: "gray", marginBottom: "2px" }}>Type</div>
                        <select
                            className='form-select form-select-sm'
                            style={{ fontSize: "11px", marginBottom: "6px" }}
                            value={currentType}
                            onChange={(e) => handleChangeColumnType && handleChangeColumnType(e, tableobject.id, ci)}
                        >
                            <option value="text">text</option>
                            <option value="toggle">toggle</option>
                            <option value="input">input</option>
                            <option value="image">image</option>
                            <option value="dropdown">dropdown</option>
                            <option value="date">date</option>
                        </select>
                        {currentType === "dropdown" && handleChangeColumnOptions && (
                            <>
                                <div style={{ fontSize: "10px", color: "gray", marginBottom: "2px" }}>Dropdown options (comma separated)</div>
                                <input
                                    className='form-control form-control-sm'
                                    style={{ fontSize: "11px", marginBottom: "6px" }}
                                    placeholder='e.g. Yes, No, Maybe'
                                    value={currentOptions}
                                    onChange={(e) => handleChangeColumnOptions(e, tableobject.id, ci)}
                                />
                            </>
                        )}
                        <div style={{ fontSize: "10px", color: "gray", marginBottom: "2px" }}>Header alignment</div>
                        <select
                            className='form-select form-select-sm'
                            style={{ fontSize: "11px", marginBottom: "6px" }}
                            value={currentHeaderAlign}
                            onChange={(e) => handleChangeColumnHeaderAlign && handleChangeColumnHeaderAlign(e, tableobject.id, ci)}
                        >
                            <option value="start">start</option>
                            <option value="center">center</option>
                            <option value="end">end</option>
                        </select>
                        <div style={{ fontSize: "10px", color: "gray", marginBottom: "2px" }}>Cell alignment</div>
                        <select
                            className='form-select form-select-sm'
                            style={{ fontSize: "11px", marginBottom: "8px" }}
                            value={currentCellAlign}
                            onChange={(e) => handleChangeColumnAlign && handleChangeColumnAlign(e, tableobject.id, ci)}
                        >
                            <option value="start">start</option>
                            <option value="center">center</option>
                            <option value="end">end</option>
                        </select>
                        <button
                            className='btn btn-sm btn-light w-100'
                            style={{ fontSize: "10px", padding: "2px 4px", border: "1px solid #ccc" }}
                            onClick={() => setColumnPickerOpen(null)}
                        >Done</button>
                    </div>,
                    document.body
                )
            })()}
        </div>
    )
}

const FixedText = (props) => {
    return (
        <div
            onClick={() => !props.preview && props.handleEdit(props.index, props.i)}
            style={{ whiteSpace: "pre-line" }}
        >
            {props.value}
        </div>
    );
}

const TextInput = ({ tableData, keyName, row, tableName, data, viewOnly }) => {
    return viewOnly ? <span style={{ borderRadius: "8px", fontSize: "12px", color: "gray" }}>{data}</span> : <input className='form-control'
        value={data && data}
        onChange={(e) => tableData && tableData(keyName, row, tableName, e.target.value)}
    />
}

const Toggle = ({ tableData, keyName, row, tableName, data, viewOnly }) => {
    return <div className="form-switch d-flex">
        <input className="form-check-input" style={{ width: "40px", height: "20px" }} type="checkbox" id="paymentTerms"
            checked={data && data}
            onChange={(e) => tableData && tableData(keyName, row, tableName, e.target.checked)}
            disabled={viewOnly}
        />
    </div>
}

const DropdownCell = ({ tableData, keyName, row, tableName, data, viewOnly, options }) => {
    return viewOnly ? <span style={{ borderRadius: "8px", fontSize: "12px", color: "gray" }}>{data}</span> :
        <select className='form-select'
            value={data && data}
            onChange={(e) => tableData && tableData(keyName, row, tableName, e.target.value)}
        >
            <option hidden value="">Select</option>
            {options && options.length > 0 ? options.map((opt, index) => (
                <option key={index} value={opt}>{opt}</option>
            )) : <>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
            </>}
        </select>
}

const DateCell = ({ tableData, keyName, row, tableName, data, viewOnly }) => {
    return viewOnly ? <span style={{ borderRadius: "8px", fontSize: "12px", color: "gray" }}>{data}</span> :
        <input type='date' className='form-control'
            value={data && data}
            onChange={(e) => tableData && tableData(keyName, row, tableName, e.target.value)}
        />
}